import Head from 'next/head'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { ArticleAuthorNotesSection } from 'editorial/components/ArticleAuthorNotesSection'
import { ArticleImageSetSection } from 'editorial/components/ArticleImageSetSection'
import { ArticleIntroductionSection } from 'editorial/components/ArticleIntroductionSection'
import { ArticleProductTemplateColumnsSection } from 'editorial/components/ArticleProductTemplateColumnsSection'
import { ArticleQuestionAnswerSection } from 'editorial/components/ArticleQuestionAnswerSection'
import { ArticleSeoTagsSection } from 'editorial/components/ArticleSeoTagsSection'
import { ArticleTextSection } from 'editorial/components/ArticleTextSection'
import { ArticleVideoSection } from 'editorial/components/ArticleVideoSection'
import { NextArticle } from 'editorial/components/NextArticle'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { serverSideConfigs, t } from 'localization'
import ErrorPage from 'pages/_error'
import { serverRequestService } from 'server/services/serverRequestService'
import { CMSArticleType } from 'shared/enums/CMSArticleType'
import CMSSectionType from 'shared/enums/CMSSectionType'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors } from 'shared/lib'
import { Article } from 'shared/types/Article'
import { ArticleSection } from 'shared/types/ArticleSection'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { ArticleFullImageSection } from 'editorial/components/ArticleFullImageSection'

const TP = 'pages.article.slug'

type ArticleSlugPageProps = {
  article: Article
  nextArticle: Article
}

const ArticleSlugPage = ({ article, nextArticle }: ArticleSlugPageProps) => {
  const router = useRouter()
  const isNextArticleEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EDITORIALS_NEXT_ARTICLE)

  if (article === null) {
    return <ErrorPage statusCode={404} />
  }

  const authorNotes =
    article?.sections?.find((section) => section?.type === CMSSectionType.AuthorNotes) || null
  const author = authorNotes ? authorNotes?.subsections?.[0]?.name : null
  const description =
    article?.description ||
    t(
      `${TP}.fallbackDescription`,
      'All of your favorite sneakers, all in one place. Shop Nike, Jordan, Adidas, and more at Flight Club.',
    )
  const getSectionComponent = (section: ArticleSection) => {
    if (!section || !section?.type) {
      return null
    }
    switch (section.type) {
      case CMSSectionType.AuthorNotes:
        return <ArticleAuthorNotesSection key={section?.uuid} section={section} />
      case CMSSectionType.FullImageSection:
        return <ArticleFullImageSection key={section?.uuid} section={section} />
      case CMSSectionType.ImageSetSection:
        return <ArticleImageSetSection key={section?.uuid} section={section} />
      case CMSSectionType.Introduction:
        return <ArticleIntroductionSection key={section?.uuid} section={section} />
      case CMSSectionType.MultipleQuestionsAndAnswersSection:
        return <ArticleQuestionAnswerSection key={section?.uuid} section={section} />
      case CMSSectionType.ProductTemplateColumnsSection:
        return <ArticleProductTemplateColumnsSection key={section?.uuid} section={section} />
      case CMSSectionType.SeoTagsSection:
        return <ArticleSeoTagsSection key={section?.uuid} section={section} />
      case CMSSectionType.TextSection:
        return <ArticleTextSection key={section?.uuid} section={section} />
      case CMSSectionType.VideoHero:
        return <ArticleVideoSection key={section?.uuid} section={section} />
      default:
        return null
    }
  }
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.flightclub.com${router?.asPath}`,
    },
    headline: article?.subject,
    description,
    image: [article?.coverPicture?.url || 'NULL'],
    datePublished: article?.publishTime,
    ...(author
      ? {
          author: {
            '@type': 'Person',
            name: author,
          },
        }
      : {}),
    publisher: {
      '@type': 'Organization',
      name: 'Flight Club',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.flightclub.com/static/flightclub_logo.svg',
        height: '750px',
        width: '750px',
      },
    },
  }

  return (
    <>
      <Head>
        <title>
          {t(`${TP}.pageTitle`, {
            defaultValue: '{title} | Flight Club',
            title: article?.subject,
          })}
        </title>
        <meta name="description" content={description} />
        <meta property="og:title" content={article?.subject} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="https://www.flightclub.com/" />
        <meta property="og:url" content={`https://www.flightclub.com${router?.asPath}`} />
        <meta property="og:image" content={article?.coverPicture?.url || 'NULL'} />
        <meta property="og:site_name" content="Flight Club" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@flightclub" />
        <meta name="twitter:title" content={article?.subject} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={article?.coverPicture?.url || 'NULL'} />
        <meta name="robots" content="max-image-preview:large" />
        <link
          rel="canonical"
          href={`https://www.flightclub.com${router?.asPath}`}
          key="canonical"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Head>
      <Page>
        {article?.sections &&
          article?.sections?.length > 0 &&
          article?.sections?.map((section) => getSectionComponent(section))}
        {isNextArticleEnabled && nextArticle && <NextArticle article={nextArticle} />}
      </Page>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, locale, params } = context

  const slug = params?.slug || ''
  try {
    const validCategories = new Set([CMSArticleType.Editorial, CMSArticleType.Greatest])
    const { data: articleData } = await serverRequestService(req, res, 'cms').get(
      `/api/v1/cms/cms_articles/${slug}`,
    )

    const nextArticleData = await (async () => {
      if (req.featureFlags?.[FeatureFlag.TEMP_WEB_FC_EDITORIALS_NEXT_ARTICLE]) {
        const { data } = await serverRequestService(req, res, 'cms').get(
          `/api/v1/cms/cms_articles/${slug}/next`,
        )
        return data
      }
      return null
    })()

    if (
      !req.featureFlags?.[FeatureFlag.TEMP_WEB_FC_EDITORIALS] ||
      !articleData ||
      (articleData?.category && !validCategories.has(articleData?.category))
    ) {
      return {
        props: {
          ...(await getServerPageProps(context)),
          ...(await serverSideConfigs(res, locale)),
          article: null,
          nextArticleData: null,
        },
      }
    }

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        article: articleData,
        nextArticle: nextArticleData,
      },
    }
  } catch (error) {
    return {
      props: {
        ...(await getServerPageProps(context)),
        ...(await serverSideConfigs(res, locale)),
        article: null,
        nextArticleData: null,
      },
    }
  }
}

const Page = styled.div`
  background-color: ${colors.FC2_WHITE};
`

export default ArticleSlugPage
